import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import { authService } from "../../services/auth.service";
import { LoginResponse } from "../../interfaces/user.interface";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { loginSchema } from "../../validations/auth.schema";

type Step = "credentials" | "otp";

const Login = () => {
  const { t } = useTranslation("auth");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<Step>("credentials");
  const [otpToken, setOtpToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const credentialsRef = useRef<{ email: string; password: string } | null>(null);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(loginSchema) });

  useEffect(() => {
    if (isAuthenticated) navigate("/");
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = () => {
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const onSubmit = async (data: { email: string; password: string }) => {
    setError("");
    setIsLoading(true);
    try {
      const result = await authService.login(data.email, data.password);
      if ("requiresOtp" in result && result.requiresOtp) {
        credentialsRef.current = { email: data.email, password: data.password };
        setOtpToken(result.otpToken);
        setStep("otp");
        startCooldown();
      } else {
        await login(result as LoginResponse);
        navigate("/");
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { message?: string }; message?: string } } };
      const msg = axiosErr.response?.data?.error?.message || axiosErr.response?.data?.message;
      setError(msg ? t(msg) : t("login.errors.invalid"));
    } finally {
      setIsLoading(false);
    }
  };

  const onVerifyOtp = async () => {
    if (otpCode.length !== 6) return;
    setError("");
    setIsLoading(true);
    try {
      const result = await authService.verifyLoginOtp(otpToken, otpCode);
      await login(result);
      navigate("/");
    } catch {
      setError(t("login.otp.invalid"));
    } finally {
      setIsLoading(false);
    }
  };

  const onResend = async () => {
    if (resendCooldown > 0 || !credentialsRef.current) return;
    setError("");
    try {
      const result = await authService.login(
        credentialsRef.current.email,
        credentialsRef.current.password,
      );
      if ("requiresOtp" in result && result.requiresOtp) {
        setOtpToken(result.otpToken);
        setOtpCode("");
        startCooldown();
      }
    } catch {
      setError(t("login.otp.resendFailed"));
    }
  };

  const inputClass = (hasError: boolean) =>
    `appearance-none relative block w-full px-3 py-2 border ${
      hasError ? "border-red-500" : "border-gray-300 dark:border-gray-600"
    } placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`;

  return (
    <Layout showSidebar={false}>
      <div className="flex min-h-[80vh] items-center justify-center">
        <div className="w-full max-w-md space-y-8 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          {step === "credentials" ? (
            <>
              <div className="text-center">
                <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                  {t("login.title")}
                </h2>
              </div>
              <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                {error && (
                  <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">{error}</div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("login.email")}
                    </label>
                    <input
                      type="email"
                      autoComplete="email"
                      {...register("email")}
                      className={inputClass(!!errors.email)}
                      placeholder={t("login.emailPlaceholder")}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{t(errors.email.message as string)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t("login.password")}
                    </label>
                    <input
                      type="password"
                      autoComplete="current-password"
                      {...register("password")}
                      className={inputClass(!!errors.password)}
                      placeholder={t("login.passwordPlaceholder")}
                    />
                    {errors.password && (
                      <p className="text-red-500 text-xs mt-1">{t(errors.password.message as string)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
                  >
                    {t("login.forgotPassword")}
                  </Link>
                </div>
                <Button
                  type="submit"
                  className="group relative flex w-full justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? t("login.signingIn") : t("login.submit")}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center">
                <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                  {t("login.otp.title")}
                </h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {t("login.otp.hint")}
                </p>
              </div>
              <div className="space-y-6">
                {error && (
                  <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">{error}</div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("login.otp.label")}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className={inputClass(false)}
                    placeholder="000000"
                    autoFocus
                  />
                </div>
                <Button
                  type="button"
                  onClick={onVerifyOtp}
                  disabled={isLoading || otpCode.length !== 6}
                  className="group relative flex w-full justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? t("login.otp.verifying") : t("login.otp.verify")}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={onResend}
                    disabled={resendCooldown > 0}
                    className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {resendCooldown > 0
                      ? t("login.otp.resendIn", { seconds: resendCooldown })
                      : t("login.otp.resend")}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setStep("credentials");
                    setError("");
                    setOtpCode("");
                  }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-center"
                >
                  ← {t("login.otp.back")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Login;
